import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import NeuralCanvas from "@/components/site/NeuralCanvas";
import { Reveal, LineReveal } from "@/components/site/Reveal";
import { WHAT_WE_DO, VIGYAVED_MODEL, MARQUEE_TERMS, IMG } from "@/data/content";

export default function Home() {
  const heroRef = useRef(null);
  const [highlights, setHighlights] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/publications`)
      .then(({ data }) => setHighlights(data.filter((p) => p.featured).slice(0, 4)))
      .catch(() => setHighlights([]));
  }, []);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
          <img src={IMG.heroNeural} alt="Vigyaved neural" className="w-full h-full object-cover object-center opacity-40 duotone" />
          <div className="absolute inset-0 bg-gradient-to-t from-vv-bg via-vv-bg/70 to-vv-bg/30" />
        </motion.div>
        <NeuralCanvas className="absolute inset-0 z-[1] w-full h-full opacity-70" />
        <div className="absolute inset-0 z-[1] spotlight" />

        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] mx-auto w-full px-6 lg:px-10 pb-20 pt-40">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="font-mono text-xs tracking-[0.3em] text-vv-accent mb-6">
            VIGYAVED HEALTHCARE — INTEGRATED · INTELLIGENT · PRECISION
          </motion.div>
          <LineReveal
            className="font-display font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-8xl leading-[0.95]"
            lines={["Building intelligent", "healthcare for longer,", "healthier lives."]}
            accentIndex={2}
          />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-2xl text-vv-muted text-base sm:text-lg leading-relaxed">
            We integrate clinical medicine, biomedical research, digital health, artificial intelligence and
            preventive health — every model and recommendation anchored in evidence-based medicine.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4">
            <Link to="/about" data-testid="hero-explore-vigyaved"
              className="group px-6 py-3 rounded-full bg-vv-accent text-black font-medium flex items-center gap-2 hover:bg-white transition-colors">
              Explore Vigyaved <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/nice" data-testid="hero-explore-nice"
              className="group px-6 py-3 rounded-full border border-vv-border text-vv-fg font-medium flex items-center gap-2 hover:border-vv-accent hover:text-vv-accent transition-colors">
              Explore NICE <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-vv-border bg-vv-surface/30 py-5">
        <Marquee speed={40} gradient={false} autoFill>
          {MARQUEE_TERMS.map((t, i) => (
            <span key={i} className="mx-8 font-display text-2xl font-medium text-vv-fg/40 flex items-center gap-8">
              {t} <span className="text-vv-accent">◆</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* WHAT WE DO — bento */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <Reveal>
          <div className="font-mono text-xs tracking-[0.3em] text-vv-accent mb-4">WHAT WE DO</div>
          <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl">
            Vigyaved operates at the intersection of medicine, science and computation — always held to the standard of evidence.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-4">
          {WHAT_WE_DO.map((c, i) => (
            <Reveal key={c.k} delay={i * 0.06}
              className={i === 0 ? "md:col-span-3 md:row-span-2" : i === 1 ? "md:col-span-3" : "md:col-span-2"}>
              <div className="group h-full bg-vv-surface border border-vv-border rounded-2xl p-8 hover:-translate-y-2 hover:border-vv-accent/50 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs text-vv-accent">{c.k}</span>
                  <ArrowUpRight size={18} className="text-vv-muted group-hover:text-vv-accent transition-colors" />
                </div>
                <h3 className={`font-display font-medium tracking-tight mt-6 ${i === 0 ? "text-3xl" : "text-xl"}`}>{c.t}</h3>
                <p className="mt-3 text-sm text-vv-muted leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VIGYAVED MODEL — numbered manifesto flow */}
      <section className="border-t border-vv-border bg-vv-surface/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="font-mono text-xs tracking-[0.3em] text-vv-accent mb-4">THE VIGYAVED MODEL</div>
              <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight">
                A pipeline from science to healthspan.
              </h2>
              <p className="mt-6 text-vv-muted leading-relaxed">
                Each stage feeds the next — turning discovery into intelligence, and intelligence into
                longer, healthier lives.
              </p>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-vv-accent hover:gap-3 transition-all">
                About the model <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            {VIGYAVED_MODEL.map((step, i) => (
              <Reveal key={step} delay={i * 0.05}>
                <div className="group flex items-center gap-6 py-5 border-b border-vv-border hover:pl-4 transition-all duration-300">
                  <span className="font-mono text-sm text-vv-muted w-10">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-2xl sm:text-4xl font-medium text-vv-fg/60 group-hover:text-vv-accent transition-colors flex-1">
                    {step}
                  </span>
                  <span className="w-2 h-2 rounded-full bg-vv-border group-hover:bg-vv-accent transition-colors" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NICE FLAGSHIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="relative rounded-3xl overflow-hidden border border-vv-border">
          <img src={IMG.hospital} alt="NICE" className="absolute inset-0 w-full h-full object-cover opacity-25 duotone" />
          <div className="absolute inset-0 bg-gradient-to-r from-vv-bg via-vv-bg/85 to-transparent" />
          <div className="relative p-10 lg:p-20 max-w-2xl">
            <Reveal>
              <div className="font-mono text-xs tracking-[0.3em] text-vv-accent mb-4">FLAGSHIP INSTITUTE</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tighter">NICE</h2>
              <p className="mt-2 text-vv-muted">Nexoria Institute of Cardiometabolic Excellence</p>
              <p className="mt-8 text-lg leading-relaxed">
                Cardiometabolic disease is not a collection of isolated diseases. It is a connected biological
                system requiring integrated prevention, prediction and treatment.
              </p>
              <Link to="/nice" data-testid="home-explore-nice"
                className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-vv-accent text-black font-medium hover:bg-white transition-colors">
                Explore NICE <ArrowRight size={18} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {highlights.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16" data-testid="home-highlights">
          <Reveal>
            <div className="font-mono text-xs tracking-[0.3em] text-vv-accent mb-4">LANDMARK PAPERS</div>
            <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight max-w-3xl">
              Selected research shaping precision cardiometabolic health.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {highlights.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <Link to="/publications" className="group block h-full bg-vv-surface border border-vv-border rounded-2xl p-6 hover:-translate-y-2 hover:border-vv-accent/50 transition-all duration-300">
                  <div className="font-display text-3xl font-extrabold tracking-tighter text-vv-accent">{p.citations || "—"}</div>
                  <div className="text-[10px] font-mono tracking-wider text-vv-muted uppercase mt-1">citations</div>
                  <h3 className="font-display text-base tracking-tight mt-5 leading-snug">{p.title}</h3>
                  <p className="text-xs text-vv-muted mt-3">{p.venue} · {p.year}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
